/**
 * `/query/v2`: the answer is published whole and every claim carries a verdict
 * (ADR-0051, ADR-0052). Transcribed from `src/aceh_rag/query_v2/models.py` and
 * `query_v2/sse.py`; the client contract is `docs-site/docs/api/query-v2.md`.
 *
 * The rule this client holds to (review C1): verification is the POSITIVE state. Only a
 * `supported` claim is styled as verified. Framing, a claim whose offsets do not land on
 * the text, and an answer whose `annotations` frame never arrived all render as "not
 * verified", never as plain text that looks the same as a verified sentence; and the
 * banner shows whenever the server's `verification` is not `full`.
 */
import { ApiError } from "./api.ts";
import { citationKey } from "./api-types.ts";
import type { Citation, ClaimCitationRef } from "./api-types.ts";

export type QueryMode = "v1" | "v2";

export type ClaimVerdict =
  | "supported"
  | "unsupported"
  | "unverified"
  | "uncited"
  | "invalid_citation"
  | "from_conversation";

export const CLAIM_VERDICTS: readonly ClaimVerdict[] = [
  "supported",
  "unsupported",
  "unverified",
  "uncited",
  "invalid_citation",
  "from_conversation",
];

export type UnverifiedReason =
  | "deadline"
  | "budget"
  | "judge_unavailable"
  | "unparseable"
  | "disputed";

/** The answer-level state the server computes (`faithfulness.verification`). */
export type V2Verification = "full" | "partial" | "none";

/** The annotation contract this client renders; anything else is shown unverified. */
export const V2_SCHEMA = "annotate-v2";

/**
 * One claim. The JSON response and the stored `synthesis.v2_claims` carry `text` and
 * resolved `citations`; the SSE `annotations` frame carries neither (the text is the
 * answer's own slice), so both are optional here. A streamed claim's citations are
 * joined from the `citation_key` each `citation` frame carries
 * (`withStreamedCitations`).
 */
export interface V2Claim {
  text?: string;
  citation_keys: string[];
  citations?: ClaimCitationRef[];
  verdict: ClaimVerdict;
  unverified_reason: UnverifiedReason | null;
  invalid_citation_keys: string[];
  kind: "fact" | "framing";
  layout: string;
  start: number;
  end: number;
  /** JavaScript string indices into the rendered Markdown `answer`. */
  start_utf16: number;
  end_utf16: number;
}

export interface V2Faithfulness {
  mechanism?: string;
  judge_status?: string;
  framing_check?: string;
  claims_total: number;
  supported: number;
  unsupported: number;
  unverified: number;
  uncited: number;
  invalid_citation: number;
  from_conversation: number;
  unverified_reasons?: Partial<Record<UnverifiedReason, number>>;
  /** `false` only when nothing was judged (`context_budget_exhausted`). Absent is NOT a
   *  pass: it means the field does not apply, and the counts decide. */
  checked?: false | null;
  framing_total?: number;
  /** Runs of answer text no claim or framing span covers; each is unverified. */
  unlocated_total?: number;
  /** Absent only on answers stored before the field existed: then the counts decide. */
  verification?: V2Verification;
  /** The claim grounding rate, as on v1's `/query`; absent on answers stored before it. */
  grounding?: { claims_supported: number; claims_total: number; grounding_rate: number | null };
}

/** The v2 part of one answer. `claims` undefined means the annotations never arrived. */
export interface V2Annotation {
  claims?: V2Claim[];
  faithfulness?: V2Faithfulness;
}

/** The SSE `annotations` frame on `/query/v2/stream`, sent BEFORE the answer text. */
export interface V2AnnotationsFrame extends V2Faithfulness {
  fallback_used: boolean;
  fallback_reason: string | null;
  claims: V2Claim[];
  framing?: Array<{ start: number; end: number; start_utf16: number; end_utf16: number; layout: string }>;
}

// ------------------------------------------------------------- mode switch --

const MODE_STORAGE_KEY = "pustaka.queryMode";

/**
 * v1 by default: the API ships `/query/v2` behind `ACEH_RAG_QUERY_V2__ENABLED=false`, so a
 * deployed client that defaulted to v2 would 404 on every question. v2 is opt-in, and
 * `localStorage` keeps the reader's choice.
 */
export function storedQueryMode(): QueryMode {
  // Feature-detect the method, not the object: Node 25 defines a method-less
  // `localStorage` (the same guard `i18n/index.ts` needs).
  const stored =
    typeof localStorage !== "undefined" && typeof localStorage.getItem === "function"
      ? localStorage.getItem(MODE_STORAGE_KEY)
      : null;
  return stored === "v2" ? "v2" : "v1";
}

export function storeQueryMode(mode: QueryMode): void {
  if (typeof localStorage !== "undefined" && typeof localStorage.setItem === "function") {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  }
}

/**
 * The 404 says the `/query/v2` route itself is absent (the server's flag is off), not
 * that the question had nothing to answer from. FastAPI's unmatched-route body is
 * `{"detail": "Not Found"}`; a proxy's non-JSON 404 reads as `HTTP 404`. The route's own
 * 404s carry the service's message, so they stay the ambiguous `queryNotFound`.
 */
export function isV2RouteMissing(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 404) return false;
  return error.message === "Not Found" || error.message === "HTTP 404";
}

/** The stored assistant message was written by `/query/v2`. */
export function isV2Message(metadata: Record<string, unknown> | undefined): boolean {
  const synthesis = metadata?.synthesis as { mode?: unknown } | undefined;
  return synthesis?.mode === "v2-annotate";
}

/**
 * Which route a conversation belongs to, or `null` while no route has answered in it.
 *
 * A conversation is pinned to the route that first answered (the API returns 409 for
 * the other one), so the toggle only chooses for an unanswered notebook. The pin and
 * each message are both read, as the API does: one metadata key is not the only record.
 */
export function conversationMode(detail: {
  metadata?: Record<string, unknown>;
  messages?: ReadonlyArray<{ role: string; metadata: Record<string, unknown> }>;
} | undefined): QueryMode | null {
  if (!detail) return null;
  if (detail.metadata?.query_version === "v2") return "v2";
  const replies = (detail.messages ?? []).filter((message) => message.role === "assistant");
  if (replies.some((message) => isV2Message(message.metadata))) return "v2";
  return replies.length > 0 ? "v1" : null;
}

// ------------------------------------------------------------ read boundary --

function count(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 ? value : 0;
}

/** Shape-checked, because this crosses the API boundary from a JSON blob. A malformed
 *  claim is dropped here and then counted as unplaceable, never rendered as verified. */
export function readV2Claims(value: unknown): V2Claim[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const claims: V2Claim[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const raw = item as Record<string, unknown>;
    const verdict = raw.verdict;
    if (typeof verdict !== "string" || !CLAIM_VERDICTS.includes(verdict as ClaimVerdict)) continue;
    const numbers = [raw.start, raw.end, raw.start_utf16, raw.end_utf16];
    if (!numbers.every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0)) continue;
    const keys = Array.isArray(raw.citation_keys)
      ? raw.citation_keys.filter((key): key is string => typeof key === "string")
      : [];
    const citations = Array.isArray(raw.citations)
      ? raw.citations
          .filter((ref): ref is Record<string, unknown> => Boolean(ref) && typeof ref === "object")
          .map((ref) => ({
            citation_key: String(ref.citation_key ?? ""),
            citation:
              ref.citation && typeof ref.citation === "object"
                ? (ref.citation as unknown as Citation)
                : null,
          }))
      : undefined;
    const reason = raw.unverified_reason;
    claims.push({
      text: typeof raw.text === "string" ? raw.text : undefined,
      citation_keys: keys,
      citations,
      verdict: verdict as ClaimVerdict,
      unverified_reason:
        reason === "deadline" ||
        reason === "budget" ||
        reason === "judge_unavailable" ||
        reason === "unparseable" ||
        reason === "disputed"
          ? reason
          : null,
      invalid_citation_keys: Array.isArray(raw.invalid_citation_keys)
        ? raw.invalid_citation_keys.filter((key): key is string => typeof key === "string")
        : [],
      kind: raw.kind === "framing" ? "framing" : "fact",
      layout: typeof raw.layout === "string" ? raw.layout : "paragraph",
      start: raw.start as number,
      end: raw.end as number,
      start_utf16: raw.start_utf16 as number,
      end_utf16: raw.end_utf16 as number,
    });
  }
  return claims;
}

export function readV2Faithfulness(value: unknown): V2Faithfulness | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const reasons = (raw.unverified_reasons ?? {}) as Record<string, unknown>;
  return {
    mechanism: typeof raw.mechanism === "string" ? raw.mechanism : undefined,
    judge_status: typeof raw.judge_status === "string" ? raw.judge_status : undefined,
    framing_check: typeof raw.framing_check === "string" ? raw.framing_check : undefined,
    claims_total: count(raw.claims_total),
    supported: count(raw.supported),
    unsupported: count(raw.unsupported),
    unverified: count(raw.unverified),
    uncited: count(raw.uncited),
    invalid_citation: count(raw.invalid_citation),
    from_conversation: count(raw.from_conversation),
    unverified_reasons: {
      deadline: count(reasons.deadline),
      budget: count(reasons.budget),
      judge_unavailable: count(reasons.judge_unavailable),
      unparseable: count(reasons.unparseable),
      disputed: count(reasons.disputed),
    },
    checked: raw.checked === false ? false : undefined,
    framing_total: count(raw.framing_total),
    unlocated_total: count(raw.unlocated_total),
    verification:
      raw.verification === "full" || raw.verification === "partial" || raw.verification === "none"
        ? raw.verification
        : undefined,
  };
}

/**
 * Streamed claims with their citations joined by key: every `citation` frame carries the
 * `citation_key` it was shown under, so the chips need not wait for the refetch. A key
 * with no frame (an invalid citation) resolves to `null`, never to a guess.
 */
export function withStreamedCitations(
  claims: ReadonlyArray<V2Claim>,
  byKey: ReadonlyMap<string, Citation>,
): V2Claim[] {
  return claims.map((claim) =>
    claim.citations !== undefined
      ? claim
      : {
          ...claim,
          citations: claim.citation_keys.map((key) => ({
            citation_key: key,
            citation: byKey.get(key) ?? null,
          })),
        },
  );
}

/**
 * A stored /query/v2 answer, from `messages[].metadata.synthesis` on
 * `GET /conversations/{id}` (`query_v2/persistence.py`): `v2_claims` with the same
 * offsets the stream sent plus each claim's text and resolved citations,
 * `v2_faithfulness`, and the fallback fields at the top level of `synthesis` (v1 keeps
 * them under `faithfulness`). `undefined` when the message is not a v2 answer.
 *
 * `claims` stays `undefined` when a published answer stored none, so it renders
 * unchecked; a replaced answer (a refusal) has no claims by contract, so it reads `[]`.
 */
export function readV2Message(metadata: Record<string, unknown> | undefined):
  | {
      claims?: V2Claim[];
      faithfulness?: V2Faithfulness;
      fallbackUsed: boolean;
      fallbackReason: string | null;
    }
  | undefined {
  if (!isV2Message(metadata)) return undefined;
  const synthesis = metadata?.synthesis as Record<string, unknown>;
  const fallbackUsed = synthesis.fallback_used === true;
  const claims = readV2Claims(synthesis.v2_claims);
  return {
    claims: claims ?? (fallbackUsed ? [] : undefined),
    faithfulness: readV2Faithfulness(synthesis.v2_faithfulness),
    fallbackUsed,
    fallbackReason: typeof synthesis.fallback_reason === "string" ? synthesis.fallback_reason : null,
  };
}

// -------------------------------------------------------------- placement --

/** A claim at its place in the answer string, with the rail cards its citations open. */
export interface V2Mark {
  start: number;
  end: number;
  verdict: ClaimVerdict;
  reason: UnverifiedReason | null;
  /** Rail numbers of the claim's resolved citations, in citation order. */
  indexes: number[];
}

/** UTF-16 index of a code-point offset, or -1 past the end. */
function utf16IndexOfCodePoint(text: string, codePoint: number): number {
  let units = 0;
  let points = 0;
  for (const char of text) {
    if (points === codePoint) return units;
    units += char.length;
    points += 1;
  }
  return points === codePoint ? units : -1;
}

/**
 * Where each claim sits in `answer`, by its UTF-16 offsets.
 *
 * When the claim carries its `text`, the slice must equal it exactly (the contract:
 * `answer[start:end] == text`). A mismatch falls back to the code-point offsets once —
 * an older writer — and otherwise the claim is UNPLACED: it gets no mark and is counted
 * on the banner, because a mark on the wrong words is worse than a visible gap. A
 * streamed claim has no text to compare, so only its bounds are checked.
 */
export function placeV2Claims(
  answer: string,
  claims: ReadonlyArray<V2Claim>,
  indexByKey: ReadonlyMap<string, number>,
): { marks: V2Mark[]; unplaced: V2Claim[] } {
  const marks: V2Mark[] = [];
  const unplaced: V2Claim[] = [];
  for (const claim of claims) {
    let start = claim.start_utf16;
    let end = claim.end_utf16;
    const inBounds = (a: number, b: number) => a >= 0 && a < b && b <= answer.length;
    if (claim.text !== undefined && (!inBounds(start, end) || answer.slice(start, end) !== claim.text)) {
      start = utf16IndexOfCodePoint(answer, claim.start);
      end = utf16IndexOfCodePoint(answer, claim.end);
      if (!inBounds(start, end) || answer.slice(start, end) !== claim.text) {
        unplaced.push(claim);
        continue;
      }
    }
    if (!inBounds(start, end)) {
      unplaced.push(claim);
      continue;
    }
    const indexes: number[] = [];
    for (const ref of claim.citations ?? []) {
      if (!ref.citation) continue;
      const index = indexByKey.get(citationKey(ref.citation));
      if (index !== undefined && !indexes.includes(index)) indexes.push(index);
    }
    marks.push({ start, end, verdict: claim.verdict, reason: claim.unverified_reason, indexes });
  }
  marks.sort((a, b) => a.start - b.start);
  return { marks, unplaced };
}

// ----------------------------------------------------------------- banner --

export type V2Status =
  /** Nothing was checked: no verdict frame, or `checked: false`. */
  | { kind: "unchecked" }
  | {
      kind: "checked";
      total: number;
      supported: number;
      /** Not supported and not from the conversation: unsupported, unverified, uncited,
       *  invalid_citation, plus claims that could not be placed on the text. */
      notVerified: number;
      fromConversation: number;
      /** Runs of answer text no annotation covers (the server's `unlocated_total`). */
      unlocated: number;
      /** `full` only when the server says so AND every supported claim is on screen. */
      verification: V2Verification;
    };

/**
 * The answer-level state the banner reports. Counts come from the faithfulness report;
 * a stored message without one falls back to counting its claims. Claims that could not
 * be placed on the text are added to `notVerified`: their mark is missing, so the reader
 * must be told rather than shown an unmarked sentence that looks like the rest.
 */
export function v2Status(annotation: V2Annotation, unplaced: ReadonlyArray<V2Claim> = []): V2Status {
  const report = annotation.faithfulness;
  if (annotation.claims === undefined || report?.checked === false) return { kind: "unchecked" };
  const tally = (verdict: ClaimVerdict) =>
    annotation.claims?.filter((claim) => claim.verdict === verdict).length ?? 0;
  const supported = report ? report.supported : tally("supported");
  const fromConversation = report ? report.from_conversation : tally("from_conversation");
  const total = report ? report.claims_total : (annotation.claims?.length ?? 0);
  const failed = report
    ? report.unsupported + report.unverified + report.uncited + report.invalid_citation
    : total - supported - fromConversation;
  // A supported or conversation claim with no mark on screen is not verified as far as
  // the reader can see. (Failed claims are already counted, placed or not.)
  const lostSupported = unplaced.filter((claim) => claim.verdict === "supported").length;
  const lostConversation = unplaced.filter((claim) => claim.verdict === "from_conversation").length;
  const shownSupported = supported - lostSupported;
  const unlocated = report?.unlocated_total ?? 0;
  // The server's verdict when it sent one (a stored answer from before the field is
  // derived the same way); never upgraded here, only lowered by what the client lost.
  const derived: V2Verification =
    supported === 0 ? "none" : supported === total && unlocated === 0 ? "full" : "partial";
  let verification = report?.verification ?? derived;
  if (shownSupported <= 0) verification = "none";
  else if (unplaced.length > 0 && verification === "full") verification = "partial";
  return {
    kind: "checked",
    total,
    supported: shownSupported,
    notVerified: failed + lostSupported + lostConversation,
    fromConversation: fromConversation - lostConversation,
    unlocated,
    verification,
  };
}

/**
 * The answer as copied into a memo: every claim that is not verified keeps its label in
 * the text. A paste that drops the marks is how an unsupported sentence becomes one that
 * reads as checked, away from the screen that marked it.
 */
export function annotateForCopy(
  answer: string,
  marks: ReadonlyArray<V2Mark>,
  label: (mark: V2Mark) => string | null,
): string {
  let out = answer;
  for (const mark of [...marks].sort((a, b) => b.end - a.end)) {
    const text = label(mark);
    if (text) out = `${out.slice(0, mark.end)} [${text}]${out.slice(mark.end)}`;
  }
  return out;
}
